import styles from "./Menu.module.css"
import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronDown } from 'lucide-react';
import { usePreferences } from "../../../context/PreferencesContext";
import { useUserContext } from "../../../context/UserContext";
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import Popup from "../../Popup/Popup";
import LanguageSwitch from "../../LanguageSwitch/LanguageSwitch";
import { useTranslation } from "react-i18next";
import { useOrderContext } from "../../../context/OrderContext";
import { handleBackgroundScroll } from "../../../utils/backgroundScroll";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
/**
* Props for the Menu component.
*/ 
interface MenuProps{
    display?:boolean, // Whether to display menu
    handleDisplay: (val: boolean) => void, // Display change event handler
    version?: "onboarding" | "default" | "settings" // Version of the menu layout
}
/**
* A menu component with customizable display and layout versions.
* Contains various links, language and currency swittch and a logout option
*
* @param props Menu props
* @param {boolean} props.display - Whether to display menu
* @param {(val: boolean) => void} props.handleDisplay - Display change event handler
* @param {"onboarding" | "default" | "settings"} props.version - Version of the menu layout
* @returns TSX.Element
*/
const Menu = ({display = false, handleDisplay, version = "default"} : MenuProps) => {
    const { t } = useTranslation(); // Translation
    const { isPublicView } = usePublicCatalog();
    const { orderData } = useOrderContext();
    const { loggedIn, logout, userData } = useUserContext(); // User data
    const { selectedCurrency, setSelectedCurrency} = usePreferences(); // User preferences (currency, language, ...)
    const navigate = useNavigate(); // Navigate
    // Flags for opening and closing the modals
    const [langOpen, setLangOpen] = useState<boolean>(false);
    const [currOpen, setCurrOpen] = useState<boolean>(false);
    const [userOpen, setUserOpen] = useState<boolean>(false);
    const [logoutOpen, setLogoutOpen] = useState<boolean>(false)
    const user = userData?.subscribers?.find(sub=> sub.subscriber_id === orderData.recipient)?.name

    // Stop scroll on open
    useEffect(()=>{
        handleBackgroundScroll(display || logoutOpen)
    }, [display, logoutOpen])

    return(
        <>
        <Popup
            title={t("navbar.logout_popup_title")}
            onClose={() => { setLogoutOpen(false); handleDisplay(true) }}
            onSubmit={()=> {logout(); handleDisplay(false); setLogoutOpen(false);}}
            backBtnText={t("buttons.no")}
            submitBtnText={t("buttons.yes")}
            display={logoutOpen}
            className={styles.LogoutPopup}
            cardClassName={styles.LogoutPopupCard}
        />
        <AnimatePresence>
            {display === true && (
            <motion.div 
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className={styles.MenuMask}
                >
            <div className={styles.Menu} >
                <div className={styles.Wrapper}>
                    <div className={styles.TitlePart}>
                        <p>Catermat.com</p>
                        <X onClick={()=>handleDisplay(false)}/>
                    </div>
                    <div className={styles.ContentPart}>
                        {version === "onboarding" && (
                            <>
                            {loggedIn ? (
                                <>
                                    <div className={styles.LinkGroup}>
                                        <div className={styles.MenuItem}>
                                            <button className={styles.MenuItemHeader} onClick={()=>navigate("/settings/user")}>
                                                <p>{t("navbar.mobile.user.settings")}</p>
                                                <ChevronRight/>
                                            </button>
                                        </div>
                                        <div className={styles.MenuItem}>
                                            <button className={styles.MenuItemHeader} onClick={()=>navigate("/settings/order")}>
                                                <p>{t("navbar.mobile.orders")}</p>
                                                <ChevronRight/>
                                            </button>
                                        </div>
                                    </div>
                                    <div className={styles.LinkGroup}>
                                        <div className={styles.MenuItem}>
                                            <button className={styles.MenuItemHeader} onClick={()=>setLangOpen(!langOpen)}>
                                                <p>{t("navbar.language")}</p>
                                                {langOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                            </button>
                                            {langOpen === true && (
                                                <LanguageSwitch type="mobile"/>
                                            ) }
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={styles.LinkGroup}>
                                        <div className={styles.MenuItem}>
                                            <button className={styles.MenuItemHeader} onClick={()=>setLangOpen(!langOpen)}>
                                                <p>{t("navbar.language")}</p>
                                                {langOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                            </button>
                                            {langOpen === true && (
                                                <LanguageSwitch type="mobile"/>
                                            ) }
                                        </div>
                                    </div>
                                </>
                            )}
                                
                            </>
                        )}
                        {version === "default" && loggedIn && !isPublicView &&(
                            <>
                                <div className={styles.LinkGroup}>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setUserOpen(!userOpen)}>
                                            <p>{user ?? "username"}</p>
                                            {userOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {userOpen === true && (
                                            <div className={styles.Submenu}>
                                                <button className={styles.SubmenuItem} onClick={()=>{navigate("/settings/order"); handleDisplay(false); setUserOpen(false)}}>{t("navbar.mobile.user.past_orders")}</button>
                                                <button className={styles.SubmenuItem} onClick={()=>{navigate("/settings/user"); handleDisplay(false); setUserOpen(false)}}>{t("navbar.mobile.user.settings")}</button>
                                                <button className={styles.SubmenuItem} onClick={()=>{navigate("/settings/address"); handleDisplay(false); setUserOpen(false)}}>{t("navbar.mobile.user.saved_adresses")}</button>
                                                <button className={styles.SubmenuItem} onClick={()=>{navigate("/settings/catalog"); handleDisplay(false); setUserOpen(false)}}>{t("navbar.mobile.user.catalog")}</button>
                                            </div>
                                            
                                        ) }
                                    </div>
                                </div>
                                <div className={styles.LinkGroup}>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setLangOpen(!langOpen)}>
                                            <p>{t("navbar.language")}</p>
                                            {langOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {langOpen === true && (
                                            <LanguageSwitch type="mobile"/>
                                        ) }
                                    </div>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setCurrOpen(!currOpen)}>
                                            <p>{t("navbar.currency")}</p>
                                            {currOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {currOpen === true && (
                                            <div className={styles.Submenu}>
                                                <button className={`${styles.SubmenuPickButton} ${selectedCurrency === "czk" ? styles.SelectedSubmenuItem: ""}`} onClick={()=>setSelectedCurrency("czk")}>czk</button>
                                                <button className={`${styles.SubmenuPickButton} ${selectedCurrency === "eur" ? styles.SelectedSubmenuItem: ""}`} onClick={()=>setSelectedCurrency("eur")}>eur</button>
                                            </div>
                                        ) }
                                    </div>
                                </div>
                            </>
                        )}
                        {version === "default" && isPublicView &&(
                            <>
                                <div className={styles.LinkGroup}>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setLangOpen(!langOpen)}>
                                            <p>{t("navbar.language")}</p>
                                            {langOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {langOpen === true && (
                                            <LanguageSwitch type="mobile"/>
                                        ) }
                                    </div>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setCurrOpen(!currOpen)}>
                                            <p>{t("navbar.currency")}</p>
                                            {currOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {currOpen === true && (
                                            <div className={styles.Submenu}>
                                                <button className={`${styles.SubmenuPickButton} ${selectedCurrency === "czk" ? styles.SelectedSubmenuItem: ""}`} onClick={()=>setSelectedCurrency("czk")}>czk</button>
                                                <button className={`${styles.SubmenuPickButton} ${selectedCurrency === "eur" ? styles.SelectedSubmenuItem: ""}`} onClick={()=>setSelectedCurrency("eur")}>eur</button>
                                            </div>
                                        ) }
                                    </div>
                                </div>
                            </>
                        )}   
                        {version === "settings" && (
                            <>
                                <div className={styles.LinkGroup}>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader}>
                                            <p>{t("navbar.mobile.user.settings")}</p>
                                            <ChevronRight/>
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.LinkGroup}>
                                    <div className={styles.MenuItem}>
                                        <button className={styles.MenuItemHeader} onClick={()=>setLangOpen(!langOpen)}>
                                            <p>{t("navbar.language")}</p>
                                            {langOpen === true ? <ChevronDown/> : <ChevronRight/>}
                                        </button>
                                        {langOpen === true && (
                                            <LanguageSwitch type="mobile"/>
                                        ) }
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {loggedIn && !isPublicView && (
                    <>
                        <div className={styles.MenuItem}>
                            <button className={styles.MenuItemHeader} onClick={()=>{setLogoutOpen(true); handleDisplay(false);}}>
                                <p>{t("navbar.logout")}</p>
                                <ChevronRight/>
                            </button>
                        </div>
                    </>
                )}
                </div>
            </motion.div>
            )}
            </AnimatePresence>
        </>
    )
};
export default Menu;